import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ExtensionManagement = ({ extensions, onRequestExtension }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    duration: '',
    justification: ''
  });

  const durationOptions = [
    { value: '4', label: '4 Hours' },
    { value: '8', label: '8 Hours' },
    { value: '24', label: '1 Day' },
    { value: '48', label: '2 Days' },
    { value: '72', label: '3 Days' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Approved': 'bg-success/10 text-success border-success/20',
      'Pending': 'bg-warning/10 text-warning border-warning/20',
      'Rejected': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onRequestExtension(formData);
    setFormData({ reason: '', duration: '', justification: '' });
    setShowForm(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={20} color="var(--color-primary)" />
          <h2 className="text-lg font-semibold text-foreground">Extension Management</h2>
        </div>
        {!showForm && (
          <Button 
            variant="outline" 
            iconName="Plus" 
            iconPosition="left"
            onClick={() => setShowForm(true)}
          >
            Request Extension
          </Button>
        )}
      </div>
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-accent/5 rounded-lg border border-accent/20">
          <h3 className="text-sm font-medium text-foreground mb-4">New Extension Request</h3>
          
          <div className="space-y-4">
            <Input
              label="Reason for Extension"
              type="text"
              placeholder="Enter reason"
              value={formData?.reason}
              onChange={(e) => setFormData({ ...formData, reason: e?.target?.value })}
              required
            />

            <Select
              label="Extension Duration"
              options={durationOptions}
              value={formData?.duration}
              onChange={(value) => setFormData({ ...formData, duration: value })}
              placeholder="Select duration"
              required
            />

            <Input
              label="Detailed Justification"
              type="text"
              placeholder="Provide detailed justification for extension"
              value={formData?.justification}
              onChange={(e) => setFormData({ ...formData, justification: e?.target?.value })}
              required
            />

            <div className="flex gap-2">
              <Button type="submit" variant="default">
                Submit Request
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {extensions?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Clock" size={48} className="mx-auto mb-2 opacity-20" />
            <p className="text-sm">No extension requests yet</p>
          </div>
        ) : (
          extensions?.map((extension) => (
            <div key={extension?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-foreground">{extension?.reason}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(extension?.status)}`}>
                      {extension?.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{extension?.justification}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>Duration: {extension?.duration}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="User" size={14} />
                  <span>Requested by: {extension?.requestedBy}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>{extension?.requestDate}</span>
                </div>
              </div>

              {extension?.approvedBy && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="CheckCircle2" size={14} color="var(--color-success)" />
                    <span>Approved by {extension?.approvedBy} on {extension?.approvalDate}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExtensionManagement;